import { PreScreening } from '../pre-screening/pre-screening.model';
import { IProExam } from '../pro-exam/pro-exam.models';
import { KyeAiNote, KyeAiNoteAuditEntry, KyeFieldFinding, KyeIdentitySnapshot, KyeRotationSnapshot } from './kye.model';

export interface GetApplicationsReq {
  examId: string;
  status: 'Completed' | 'In Progress' | 'Not Started' | 'Pending Review' | 'Accepted' | 'All';
  decisionMade?: string;
}

export interface MarkAttendanceReq {
  applicationId: string;
  comment?: string;
  status: 'Present' | 'Absent' | 'Excused' | 'AWOL';
  mmiCohortId: string;
}

export interface ProApplicationStep {
  name: string;
  data: any;
  finished?: boolean;
}

export interface ProApplicationReviewStep {
  name: string;
  steps: any;
  adminData: any;
}

export enum ProApplicationStatusEnum {
  NotStarted = 'Not started',
  PendingReview = 'Pending Review',
  InProgress = 'In Progress',
  Submitted = 'Submitted',
  InReview = 'In Review',
  Reviewed = 'Reviewed',
  Completed = 'Completed',
  /** An unsubmitted application pulled out of the process. It stays in the
   *  applicant's list but can no longer be continued, edited or submitted.
   *  Candidature non soumise retirée : visible, mais figée. */
  Withdrawn = 'Withdrawn',
}

/**
 * True when either phase of the application has been withdrawn — `status` for
 * phase 1, `phase2Status` for phase 2. Mirrors the backend's
 * `isApplicationWithdrawn`, which refuses every applicant write on such an
 * application; this side keeps the applicant from reaching the forms at all.
 *
 * Vrai lorsque l'une ou l'autre phase de la candidature a été retirée.
 */
export function isApplicationWithdrawn(application: Pick<ProUserApplication, 'status' | 'phase2Status'> | null | undefined): boolean {
  return application?.status === ProApplicationStatusEnum.Withdrawn || application?.phase2Status === ProApplicationStatusEnum.Withdrawn;
}

/**
 * True while the applicant is still filling in either phase — `status` (phase 1)
 * or `phase2Status` (phase 2) is In Progress — and the application has not been
 * withdrawn. Such an applicant must finish (or withdraw) it before applying to
 * another exam. Mirrors the backend's `isApplicationInProgress`, which refuses
 * to start a second application.
 *
 * Vrai tant que l'une ou l'autre phase de la candidature est en cours (et non
 * retirée) ; le candidat ne peut alors pas postuler à un autre examen.
 */
export function isApplicationInProgress(application: Pick<ProUserApplication, 'status' | 'phase2Status'> | null | undefined): boolean {
  if (application == null || isApplicationWithdrawn(application)) return false;
  return application.status === ProApplicationStatusEnum.InProgress || application.phase2Status === ProApplicationStatusEnum.InProgress;
}

/**
 * The applicant's current application out of the list `GET /applications/user`
 * returns. That list puts the applications they withdrew first (kept as their
 * history) and the current one LAST, so the first entry may be a withdrawn
 * application the server refuses to continue.
 *
 * La candidature actuelle du candidat : la DERNIÈRE de la liste renvoyée.
 */
export function currentApplication<T>(applications: readonly T[] | null | undefined): T | undefined {
  return applications?.length ? applications[applications.length - 1] : undefined;
}

export interface IEligibility {
  status: 'Eligible' | 'Ineligible' | 'Eligible - not proceeding to stage 2' | 'Referral' | '';
  reasons: string[];
  flags: string[];
}

/** Language in which the applicant completed this application. */
export type ApplicationLanguage = 'en' | 'fr';

/** Application phase targeted when saving stream language. */
export type ApplicationPhase = 'phase1' | 'phase2';

/** Phase-2 areas that collect a referee the applicant can email a reminder to.
 *  Matches the `step` discriminator of POST /application/phase2/resend-referee-email. */
export type RefereeStep = 'referee' | 'rotation-equivalence' | 'currency' | 'independent-practice';
/**
 * One referee of a phase-2 application, whichever of the four reference
 * workflows it came from, as returned by
 * `GET /application/phase2/referees/{applicationNo}`. Backs the "Referees
 * Status" dialog, which tables all four workflows together.
 *
 * Un répondant d'une candidature de phase 2, quel que soit le flux d'origine.
 */
export interface ApplicationReferee {
  /** Mongo id of the underlying record — the `refereeId` a resend takes.
   *  Identifiant du document — le `refereeId` attendu par le renvoi. */
  id: string;
  step: RefereeStep;
  /** Human-readable name of the workflow, e.g. "Currency of Practice". */
  typeLabel: string;
  name: string;
  email: string;
  position: string;
  phone: string;
  /** Practice/institution the reference relates to, where the workflow has one. */
  organisation: string;
  /** True once the referee has submitted their form / Vrai une fois le formulaire soumis. */
  completed: boolean;
  submittedAt: string | null;
  /** When the referee was first invited / Date de la première invitation. */
  invitedAt: string | null;
  lastReminderSentAt: string | null;
  notes: string[];
  /** The submitted form, or null while the referee has yet to fill it in.
   *  Le formulaire soumis, ou null tant qu'il n'est pas rempli. */
  form: any | null;
}


/** A portal draw link: the draw's id, or the draw populated with its name, or
 *  null/absent when the application was never drawn (or left its draw).
 *  Lien vers un tirage : identifiant, tirage peuplé avec son nom, ou null. */
export type ApplicationDrawRef = string | { _id?: string; drawName?: string } | null;

export interface ProUserApplication {
  id: string;
  /** Mongo ObjectId as returned by list endpoints (e.g. /applications/all).
   *  Identifiant Mongo tel que renvoyé par les endpoints de liste. */
  _id?: string;
  user: IUser | string;
  exam: IProExam;
  phase2Exam: IProExam;
  flags: string[];
  status: ProApplicationStatusEnum;
  phase2Status: ProApplicationStatusEnum;
  applicationNo: string;
  preScreening: PreScreening;
  eligibility: IEligibility;
  phase2Eligibility: IEligibility;
  phase1DecisionMade: any;
  phase2DecisionMade: any;
  steps: ProApplicationStep[];

  /**
   * TDM (Therapeutics Decision Making) fee payment tracking.
   * Suivi du paiement des frais d'examen TDM (prise de décision thérapeutique).
   */
  tdmPayment?: {
    invoiceId?: string;
    status?: 'pending' | 'paid' | 'refunded' | 'waived';
    amount?: number;
    paidAt?: string;
    refundedAt?: string;
    refundReason?: string;
    refundedBy?: string;
  };
  phase2ApplicationSteps: Phase2ApplicationSteps;
  phase1SubmittedDate: string;
  phase2SubmittedDate: string;

  /** The portal draws the application was selected in — Phase 1, and Phase 2
   *  once it has reached it. The screener queue populates them with the draw's
   *  name; other endpoints return the bare id or leave them out.
   *  Tirages de la candidature (phase 1, puis phase 2) ; peuplés avec le nom
   *  par la file d'examen. */
  portalDrawId?: ApplicationDrawRef;
  phase2PortalDrawId?: ApplicationDrawRef;

  /** The candidate's previous applications — earlier years, other sessions of
   *  the same year and earlier attempts in this session, whatever their status —
   *  one row per phase, attached by the list endpoints so the dialog opens
   *  without a fetch.
   *  Candidatures antérieures du candidat, une ligne par phase. */
  previousApplications?: PreviousApplication[];
  /** Number of rows in {@link previousApplications} — the table's sortable column.
   *  Nombre de candidatures antérieures — colonne triable du tableau. */
  previousApplicationsCount?: number;
  /** Review entries of the phase being listed, newest first, attached by the
   *  list endpoints so the dialog opens without a fetch.
   *  Entrées de révision de la phase affichée, la plus récente en premier. */
  reviewHistory?: ReviewHistoryEntry[];
  /** Number of review entries recorded for the phase being listed.
   *  Nombre de révisions enregistrées pour la phase affichée. */
  reviewHistoryCount?: number;
  /** Whether the Phase 1 decision email has been sent for this application. */
  sentPhase1DecisionEmails?: boolean;
  /** Whether the Phase 2 decision email has been sent for this application. */
  sentPhase2DecisionEmails?: boolean;
  /** Screener queue only — the phase the queue shows the application in: Phase 2
   *  when, and only when, `phase2PortalDrawId` links to a draw that exists,
   *  Phase 1 otherwise; on a list page with a Phase filter on, the filtered phase.
   *  File d'examen — phase affichée : 2 seulement si le tirage de phase 2 existe. */
  queuePhase?: 1 | 2;
  /** Screener queue processing page only — the phases whose decision stands
   *  finalized (signed off, not reopened since), computed server-side from the
   *  full timeline so every role gets it. Locks the decision form for that phase.
   *  Page de traitement — phases dont la décision est finalisée. */
  finalizedPhases?: (1 | 2)[];
  /** Screener queue list only — the phase whose decision the queue shows (the
   *  row's `queuePhase`), and that decision: the latest on its timeline, else
   *  the stored one; '' for none.
   *  File d'examen — phase de la décision affichée, et cette décision. */
  decisionPhase?: 1 | 2;
  decision?: string;
  /** Phase 2 list views only — "completed / total" references of each of the
   *  four reference workflows ("2 / 3"), counted server-side.
   *  Listes de phase 2 — références « remplies / totales » de chaque flux. */
  referees?: string;
  independentPractices?: string;
  currencyOfPractices?: string;
  rotationEquivalencies?: string;
  /** Language the applicant completed this application in, set when the
   *  application is created. Absent on applications that predate the field —
   *  they are English by the schema's default, which is how the queue tables
   *  and the server-side language filter both count them.
   *  Langue de rédaction ; absente sur les candidatures antérieures au champ. */
  language?: ApplicationLanguage;

  /** Phase B — Kye AI Agents (advisory; see kye.model.ts).
   *  Phase B — Agents IA Kye (consultatif ; voir kye.model.ts). */
  aiNotes?: KyeAiNote[];
  /** Phase C — append-only audit trail of screener actions on aiNotes[].
   *  Phase C — journal d'audit en ajout-seul des actions sur les notes IA. */
  aiNoteAuditTrail?: KyeAiNoteAuditEntry[];
  kyeIdentityReview?: KyeIdentitySnapshot | null;
  kyeRotationReview?: KyeRotationSnapshot | null;
  /** Phase D — inline field-level findings projected from structured KYE output.
   *  Advisory only. Append-only per run (keyed by findingId).
   *  Phase D — constats au niveau du champ projetés depuis la sortie structurée KYE. */
  kyeFieldFindings?: KyeFieldFinding[];

  /** Mongo timestamp — used by the Kye header to detect that the application
   *  has been modified since the most recent AI run (staleness badge).
   *  Horodatage Mongo — utilisé par l'en-tête Kye pour détecter qu'une
   *  candidature a été modifiée depuis la dernière exécution IA. */
  updatedAt?: string;

  /** True when a PhysiciansApply record has been linked to this application
   *  (`physicansApplyData.candidateCode2` is populated). Computed server-side
   *  by `ScreenerQueueService.getApplication` and injected into the response.
   *  The raw PA data is excluded from API responses for PII reasons.
   *
   *  Vrai lorsqu'un dossier PhysiciansApply est lié à cette candidature.
   *  Calculé côté serveur et injecté dans la réponse. */
  hasPaData?: boolean;

  /** Flag Status column (list views only) — total/resolved counts of raised
   *  flag documents (`FlagProApplication`, keyed by applicationNo), computed
   *  server-side by the list endpoint. Not the same concept as `flags`
   *  above, which is a plain string tag list.
   *  Colonne Statut des signalements — comptes total/résolu des documents
   *  de signalement, calculés côté serveur. */
  totalFlagCount?: number;
  resolvedFlagCount?: number;
}

export interface IUser {
  email: string;
  firstName: string;
  language?: string;
  lastName: string;
  _id: string;
}

/** One entry of an application's review audit trail, as stored on
 *  `reviewHistory` server-side and served by `/applications/review-history`.
 *  Une entrée de la piste d'audit des révisions d'une candidature. */
export interface ReviewHistoryEntry {
  reviewedAt: string;
  /** Absent on QC entries, which carry `qcReview` instead. */
  decision?: string;
  /** Legacy display name — prefer `authorName` when present. */
  author: string;
  authorId?: string;
  authorName?: string;
  authorRole?: string;
  /** Phase the entry belongs to; untagged legacy entries count as phase 1. */
  phase?: 1 | 2;
  /** Workflow step that produced the entry (admin-decision, qc-review…). */
  source?: string;
  /** Application step key when recorded from a step review page. */
  applicationStep?: string;
  comment?: string;
  reasons?: string[];
  referralReasons?: string[];
  conditions?: string[];
  /** Unmet minimum-eligibility criteria, on an Ineligible decision. */
  ineligibleCriterias?: string[];
  decisionStage?: 'preliminary' | 'final';
  noteEn?: string;
  noteFr?: string;
  finalizedAt?: string;
  finalizedBy?: string;
  qcReview?: {
    overallResult: string;
    escalationReason?: string;
    findingsCount: number;
    flaggedCount: number;
    confirmedCount: number;
    qcReviewIndex: number;
  };
}

/** A row of the applicant's earlier applications, one per phase of every
 *  application submitted in an exam session held before the current one.
 *  Une candidature antérieure du même candidat, une ligne par phase. */
export interface PreviousApplication {
  applicationNo: string;
  reviewStatus: string;
  applicationPhase: string;
  applicationSession?: string;
  applicationYear: string;
  examSessionId: string;
  applicationStatus: string;
}

export interface ProApplication {
  id: string;
  _id: string;
  user: string;
  // examSessionId: string;
  exam: string;
  applicationNo: string;
  status: string;
  steps: ProApplicationStep[];
  phaseTwoSteps: ProApplicationStep[];
  eligibility: {
    status: 'Eligible' | 'Ineligible' | 'Eligible - not proceeding to stage 2' | '';
    reasons: string[];
    flags: string[];
  };
  examSessionId: IProExam;
  preScreening: PreScreening;
}

export interface ProApplicationReview {
  _id: string;
  id: string;
  applicationNumber: string;
  firstName: string;
  lastName: string;
  applicationStatus: string;
  currencyOfPractices: string;
  examId: string;
  flagCount: number;
  independentPractices: string;
  isFlagged: boolean;
  noOfPreviousApplication: number;
  referees: string;
  resolvedFlagCount: number;
  reviewStatus: string;
  session: string;
  status: string;
  userId: string;
  year: string;
  application: ProApplicationReviewStep[];
}

export interface Phase2ApplicationSteps {
  personalInformation: PersonalInformationStep;
  certificationsQualifications: CertificationsQualificationsStep;
  languageProficiency: LanguageProficiencyStep;
  postgraduateTraining: PostgraduateTrainingStep;
  rotationEquivalencies?: RotationEquivalenciesStep;
  independentPracticeClinicalExperience: IndependentPracticeClinicalExperienceStep;
  clinicalPracticeSettings: ClinicalPracticeSettingsStep;
  ipPatientPopulation: IpPatientPopulationStep;
  currencyPracticeClinicalExperience: CurrencyPracticeClinicalExperienceStep;
  currencyOfPracticeSettings: CurrencyOfPracticeSettingsStep;
  currencyOfPracticePatientPopulations: CurrencyOfPracticePatientPopulationsStep;
  references: ReferencesStep;
  informationSharing: InformationSharingStep;
}

export interface Phase2ApplicationAdminSteps {
  personalInformation?: PersonalInformationStep;
  certificationsQualifications?: CertificationsQualificationsStep;
  languageProficiency?: LanguageProficiencyStep;
  postgraduateTraining?: PostgraduateTrainingStep;
  independentPracticeClinicalExperience?: IndependentPracticeClinicalExperienceStep;
  clinicalPracticeSettings?: ClinicalPracticeSettingsStep;
  ipPatientPopulation?: IpPatientPopulationStep;
  rotationEquivalencies?: RotationEquivalenciesStep;
  currencyPracticeClinicalExperience?: CurrencyPracticeClinicalExperienceStep;
  currencyOfPracticeSettings?: CurrencyOfPracticeSettingsStep;
  currencyOfPracticePatientPopulations?: CurrencyOfPracticePatientPopulationsStep;
  references?: ReferencesStep;
  informationSharing?: InformationSharingStep;
}

export interface PersonalInformationStep {
  name: string;
  completed: boolean;
  userData: {
    firstName: string;
    middleName: string;
    lastName: string;
    previousNames: string;
    email: string;
    gender: string;
    specificGender: string;
    dateOfBirth: string;
    legalStatus: string;
    citizenship: string;
    countryOfBirth: string;
    motherTongue: string;
    phoneNumber: string;
    streetAddress: string;
    unitNumber: string;
    country: string;
    state: string;
    city: string;
    zipCode: string;
    physiciansApplyCandidateCode: string;
    mincId: string;
    avatar: string;
  };
  adminData: {
    comment: string;
  };
}

export interface CertificationsQualificationsStep {
  name: string;
  completed: boolean;
  userData: {
    advancedCardio: string;
    advancedTrauma: string;
    pediatricAdvanced: string;
    cardioDateOfCompletion: string | null;
    traumaDateOfCompletion: string | null;
    pediatricDateOfCompletion: string | null;
    certifications: Array<{
      nameOfCertification: string;
      briefDescription: string;
      dateOfCompletion: string;
    }>;
  };
  adminData: {
    comment: string;
  };
}

export interface LanguageProficiencyStep {
  name: string;
  completed: boolean;
  userData: {
    fileUpload: string;
    undergraduate: boolean;
    practiceCountry: boolean;
    successfulCompletion: boolean;
    explanationIfNoProof: string;
  };
  adminData: {
    comment: string;
  };
}

export interface PostgraduateTrainingStep {
  name: string;
  completed: boolean;
  userData: {
    pgtFamilyMedicine: PostgraduateTrainingRotation;
    pgtEmergencyMedicine: PostgraduateTrainingRotation;
    pgtGeneralSurgery: PostgraduateTrainingRotation;
    pgtInternalMedicine: PostgraduateTrainingRotation;
    pgtObstetrics: PostgraduateTrainingRotation;
    pgtPediatrics: PostgraduateTrainingRotation;
    pgtPsychiatry: PostgraduateTrainingRotation;
    pgtOthers: {
      others: Array<PostgraduateTrainingRotation & { rotationName: string }>;
    };
    nameOfMedicalSchoolAdmin: string;
    durationOfTrainingProgram: string;
    programStartDate: string;
    programCompletedDate: string;
  };
  adminData: {
    comment: string;
  };
}

export interface PostgraduateTrainingRotation {
  trainingLocation: string;
  otherTrainingLocation: string | null;
  clinic: string;
  city: string;
  province: string;
  country: string;
  startDate: string;
  endDate: string;
  numberOfWeeks: string;
  directCare: string;
  explainCareYouProvided: string | null;
  rotationCompleted: string;
  medicalSchool: string | null;
}

export interface IndependentPracticeClinicalExperienceStep {
  name: string;
  completed: boolean;
  userData: {
    clinicalExperiences: ClinicalExperience[];
  };
  adminData: {
    comment: string;
  };
}

export interface ClinicalExperience {
  _id?: any;
  application?: any;
  nameOfPractice: string;
  key: string;
  roleTitle: string;
  city: string;
  province: string;
  country: string;
  supervisorFirstName: string;
  supervisorLastName: string;
  supervisorTitle: string;
  supervisorEmail: string;
  supervisorNumber: string;
  supervisorSecondaryNumber: string;
  startDate: any;
  endDate: any;
  hoursOfWork: number;
  timeOff: number;
  totalHoursWorked: number;
  clarificationNotes: string | null;
  virtualBasedCare: string;
  responsiblePhysician: string;
  briefDescription: string;
  typeOfPractice: string;
  otherTypeOfPractice: string | null;
  averageVirtualBasedCare: number | null;
  explainDurationOfEmployment: string | null;
  patientReferral: string | null;
  relationship: string;
  notes: any[];
  createdAt?: any;
  updatedAt?: any;
  __v?: number;
}

export interface ClinicalPracticeSettingsStep {
  name: string;
  completed: boolean;
  userData: {
    ambulatoryPractice: string;
    homeVisitOutreach: string;
    hospitalInpatient: string;
    hospitalEmergency: string;
    residentialLongTermCare: string;
    other: string;
    description: string;
    total: number;
  };
  adminData: {
    comment: string;
  };
}

export interface RotationEquivalenciesStep {
  name: string;
  completed: boolean;
  userData: RotationEquivalency[];
  adminData: {
    comment: string;
  };
}

export interface RotationEquivalency {
  _id: string;
  id: string;
  inPersonHours: any[];
  virtualHours: any[];
  notes: any[];
  rotationOneStatus: string;
  rotationName: string;
  applicationId: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
  coverScopeOfMissingRotation?: boolean;
  metThroughEquivalency?: string | null;
  explanationForNoAttestation?: string | null;
  applicantRoleOrTitle?: string;
  descriptionOfCareProvided?: string;
  emailAddressOfReferee?: string;
  endDate?: string;
  firstNameOfReferee?: string;
  lastNameOfReferee?: string;
  nameOfPractice?: string;
  phoneOfReferee?: string;
  positionOfReferee?: string;
  practiceLocation?: string;
  startDate?: string;
}

export interface IpPatientPopulationStep {
  name: string;
  completed: boolean;
  userData: {
    firstContact: boolean;
    patientCare: boolean;
    reproductiveCare: boolean;
    antenatalCare: boolean;
    lowRisk: boolean;
    postpartumCare: boolean;
    newbornHospital: boolean;
    newbornCommunity: boolean;
    comprehensiveCareYouth: boolean;
    comprehensiveCareAdults: boolean;
    comprehensiveCareOlderAdults: boolean;
    palliativeCare: boolean;
    addictionCare: boolean;
    patientsDepartment: boolean;
    patientsOffice: boolean;
    patientsHome: boolean;
    patientsSettings: boolean;
    patientsHomes: boolean;
    careFacilities: boolean;
    communitySettings: boolean;
    responsiblePhysician: boolean;
    performProcedures: boolean;
    medicalCommunity: boolean;
    attestationOne: boolean;
    attestationTwo: boolean;
  };
  adminData: {
    comment: string;
  };
}

export interface CurrencyPracticeClinicalExperienceStep {
  name: string;
  completed: boolean;
  userData: {
    currencyOfPracticeExperiences: ClinicalExperience[];
  };
  adminData: {
    comment: string;
  };
}

export interface CurrencyOfPracticeSettingsStep {
  name: string;
  completed: boolean;
  userData: {
    ambulatoryPractice: string;
    homeVisitOutreach: string;
    hospitalInpatient: string;
    hospitalEmergency: string;
    residentialLongTermCare: string;
    other: string;
    description: string;
    total: number;
  };
  adminData: {
    comment: string;
  };
}

export interface CurrencyOfPracticePatientPopulationsStep {
  name: string;
  completed: boolean;
  userData: {
    firstContact: boolean;
    patientCare: boolean;
    reproductiveCare: boolean;
    antenatalCare: boolean;
    lowRisk: boolean;
    postpartumCare: boolean;
    newbornHospital: boolean;
    newbornCommunity: boolean;
    comprehensiveCareYouth: boolean;
    comprehensiveCareAdults: boolean;
    comprehensiveCareOlderAdults: boolean;
    palliativeCare: boolean;
    addictionCare: boolean;
    patientsDepartment: boolean;
    patientsOffice: boolean;
    patientsHome: boolean;
    patientsSettings: boolean;
    patientsHomes: boolean;
    careFacilities: boolean;
    communitySettings: boolean;
    responsiblePhysician: boolean;
    performProcedures: boolean;
    medicalCommunity: boolean;
    attestationOne: boolean;
    attestationTwo: boolean;
  };
  adminData: {
    comment: string;
  };
}

export interface ReferencesStep {
  name: string;
  completed: boolean;
  userData: {
    referees: Referee[];
  };
  adminData: {
    comment: string;
  };
}

export interface Referee {
  _id?: any;
  key: string;
  salutation: string;
  firstName: string;
  lastName: string;
  position: string;
  primaryTelephone: string;
  secondaryTelephone: string;
  emailAddress: string;
  supervised: string;
  briefDescription: string | null;
  application?: any;
  startDate: any;
  endDate: any;
  notes: any[];
  createdAt?: any;
  updatedAt?: any;
  __v?: number;
}

export interface InformationSharingStep {
  name: string;
  completed: boolean;
  userData: {
    authorization: boolean;
    contentToShare: boolean;
    attestation: boolean;
    acknowledgement: boolean;
    understanding: boolean;
    disclaimer: boolean;
  };
  adminData: {
    comment: string;
  };
}
